import {List, Tooltip} from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress, {circularProgressClasses} from '@mui/material/CircularProgress';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {Link} from 'react-router-dom';

import {APP_ROUTES, BREAKPOINTS} from '../../../../const';
import useBreakpoint from '../../../../hooks/dom/useBreakpoint';
import usePayment from '../../../../hooks/providers/usePayment';
import useProfile from '../../../../hooks/providers/useProfile';
import {normaliseValue} from '../../../../utils';
import ContactEmailsList from '../../../contact/ContactEmailsList/ContactEmailsList';
import UpgradeSubscriptionEmailsList from '../../../contact/UpgradeSubscriptionEmailsList/UpgradeSubcriptionEmailsList';

const StyledLink = styled(Link)`
  &:visited {
    color: #90a8fc;
  }
`;

const RoundedBordersCircularProgress = styled(CircularProgress)(() => ({
  [`& .${circularProgressClasses.svg}`]: {
    strokeLinecap: 'round'
  }
}));

const getCounterCssColor = normalizedValue => {
  if (normalizedValue < 80) {
    return '#2BC5AB';
  }
  if (normalizedValue >= 80 && normalizedValue < 100) {
    return '#EA9C1F';
  }
  return '#DB434F';
};

// eslint-disable-next-line complexity
const getNumberOfProjectsFontSize = (maxNumberOfProjects, breakpoint) => {
  if (maxNumberOfProjects >= 100 && maxNumberOfProjects <= 999) {
    return breakpoint === BREAKPOINTS.xs ? 14 : 15;
  }
  if (maxNumberOfProjects >= 1000 && maxNumberOfProjects <= 9999) {
    return breakpoint === BREAKPOINTS.xs ? 12 : 13;
  }
  if (maxNumberOfProjects >= 10000 && maxNumberOfProjects <= 99999) {
    return breakpoint === BREAKPOINTS.xs ? 10 : 11;
  }
  return breakpoint === BREAKPOINTS.xs ? 14 : 16;
};

// eslint-disable-next-line complexity
const getCounterSize = (maxNumberOfProjects, breakpoint) => {
  if (maxNumberOfProjects >= 100 && maxNumberOfProjects <= 999) {
    return breakpoint === BREAKPOINTS.xs ? 65 : 75;
  }
  if (maxNumberOfProjects >= 1000 && maxNumberOfProjects <= 9999) {
    return breakpoint === BREAKPOINTS.xs ? 75 : 85;
  }

  if (maxNumberOfProjects >= 10000 && maxNumberOfProjects <= 99999) {
    return breakpoint === BREAKPOINTS.xs ? 85 : 95;
  }
  return breakpoint === BREAKPOINTS.xs ? 55 : 70;
};

const ConnectionsCounter = () => {
  const {maxNumberOfProjects, subscriptionContactInfo, numberOfProjectsOwnedByUser, numberOfProjectsOwnedByAllUsers, isPartner} = usePayment();
  const {profile, isPartnerPolicy} = useProfile();

  const value = normaliseValue(numberOfProjectsOwnedByAllUsers, maxNumberOfProjects);

  const color = getCounterCssColor(value);
  const breakpoint = useBreakpoint();
  const size = getCounterSize(maxNumberOfProjects, breakpoint);
  const thickness = breakpoint === BREAKPOINTS.xs ? 3 : 4;

  const renderNumberOfProjectsText = () => {
    if (!profile) return null;

    const numberFontSize = getNumberOfProjectsFontSize(maxNumberOfProjects, breakpoint);

    return (
      <Typography lineHeight={1} variant="caption" component="div" fontSize={numberFontSize} fontWeight="bold">
        {`${numberOfProjectsOwnedByAllUsers} / ${maxNumberOfProjects}`}
      </Typography>
    );
  };

  const getTooltipText = () => {
    const connectionWord = numberOfProjectsOwnedByUser > 1 ? 'connexions' : 'connexion';
    const connectionsSentence = `Vous détenez ${numberOfProjectsOwnedByUser} ${connectionWord}. Un dossier peut avoir plusieurs connexions.`;

    if (isPartner) {
      return (
        <div style={{lineHeight: 1.3}}>
          {connectionsSentence} <UpgradeSubscriptionEmailsList />
        </div>
      );
    }

    if (isPartnerPolicy) {
      return connectionsSentence;
    }

    const normalUserDrivnPolicyMessage = (
      <div style={{lineHeight: 1.3}}>
        {connectionsSentence} <StyledLink to={APP_ROUTES.subscription}>Augmenter la limite de mon abonnement</StyledLink>.
      </div>
    );
    return normalUserDrivnPolicyMessage;
  };

  const counterTooltip = getTooltipText();

  return (
    <Tooltip placement="top" arrow title={counterTooltip}>
      <Box sx={{position: 'relative', display: 'inline-flex'}}>
        <RoundedBordersCircularProgress data-testid="progressbar" size={size} variant="determinate" thickness={thickness} value={value} sx={{color, zIndex: 1}} />
        <RoundedBordersCircularProgress size={size} variant="determinate" thickness={thickness} value={100} sx={{position: 'absolute', color: 'lightgrey', zIndex: 0}} />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          {renderNumberOfProjectsText()}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default ConnectionsCounter;
